<template>
  <v-container fluid>
    <h2 class="mb-5">CSVダウンロード</h2>

    <v-card max-width="800" class="mx-auto">
      <v-card-text>

        <h4 class="pa-1">作成リクエスト一覧</h4>
        <v-divider class="my-2"></v-divider>

        <v-row class="my-3">
          <v-col cols="12" v-if="CSVRequests.length == 0">
            <p class="ms-3">なし</p>
          </v-col>

          <v-col cols="12" v-for="request in CSVRequests" v-bind:key="request.requestID">
            <v-card variant="outlined" class="mx-3">
              <v-card-text>
                <v-row>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 5">
                    <v-label class="text-subtitle-2">作成リクエスト日時</v-label>
                  </v-col>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 7">
                    <span class="ms-1">{{ request.requestDatetime }}</span>
                  </v-col>

                  <v-col cols="5">
                    <v-label class="text-subtitle-2">モジュール</v-label>
                  </v-col>
                  <v-col cols="7">
                    <span class="ms-1">
                      {{ (request.moduleID === '#') ? 'すべて' : request.moduleID }}
                    </span>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 5">
                    <v-label class="text-subtitle-2">出力対象期間</v-label>
                  </v-col>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 7">
                    <p class="ms-1">
                      {{ request.targetDatetime.start }}<br v-if="$vuetify.display.mobile">
                      &nbsp;～&nbsp;{{ request.targetDatetime.end }}
                    </p>
                  </v-col>

                  <v-col cols="5" class="d-flex align-center">
                    <v-label class="text-subtitle-2">ステータス</v-label>
                  </v-col>
                  <v-col cols="7">
                    <v-chip size="small" :color="(request.status === 'ダウンロード可能') ? 'success' : (request.status === '作成失敗') ? 'error' : ''">
                      <template v-slot:prepend>
                        <v-progress-circular indeterminate size="17" width="2"
                          class="me-2" v-if="request.status === '作成中'"></v-progress-circular>
                      </template>
                      {{ request.status }}
                    </v-chip>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 5">
                    <v-label class="text-subtitle-2">ダウンロード有効期限</v-label>
                  </v-col>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 7">
                    <span class="ms-1">
                      {{ (request.expirationDatetime != null) ? request.expirationDatetime : '-' }}
                    </span>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions class="d-flex justify-end">
                <v-btn color="primary" prepend-icon="mdi-download" :disabled="request.downloadURL == null"
                  :loading="request.isDownloadStartLoading" v-on:click="download(request);">
                  ダウンロード
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <v-col cols="12" class="d-flex justify-end">
            <v-btn variant="outlined" prepend-icon="mdi-reload" class="me-3"
              :loading="isGetListLoading" v-on:click="fetchCSVRequestList">
              最新状況を確認
            </v-btn>
          </v-col>
        </v-row>

        <h4 class="pa-1">新規ダウンロード</h4>
        <v-divider class="my-2"></v-divider>

        <v-form @submit.prevent ref="form" class="mt-5">
          <v-row>
            <v-col cols="12">
              <v-label class="ms-3 text-subtitle-2">モジュール選択</v-label>
            </v-col>
            <v-col :cols="$vuetify.display.mobile ? 10 : 5">
              <v-select variant="outlined" density="compact" class="ms-4"
                :rules="[ v => (v !== '') || '必須' ]"
                persistent-hint hint="必須"
                v-model:model-value="moduleID" :items="modules"></v-select>
            </v-col>

            <v-col cols="12">
              <v-label class="ms-3 text-subtitle-2">出力対象期間（開始）</v-label>
            </v-col>
            <v-col :cols="$vuetify.display.mobile ? 10 : 5">
              <vue-date-picker v-model="beginDatetime" class="ms-4"
                locale="ja" week-start="0" auto-apply
                minutes-grid-increment="1" enable-seconds seconds-grid-increment="1" :teleport="true"
                :format="formatDatetime" :clearable="false"
                :state="(isShowPeriodLargeErrorMessage ? false : null)"></vue-date-picker>
            </v-col>

            <v-col cols="12">
              <v-label class="ms-3 text-subtitle-2">出力対象期間（終了）</v-label>
            </v-col>
            <v-col :cols="$vuetify.display.mobile ? 10 : 5">
              <vue-date-picker v-model="endDatetime" class="ms-4"
                locale="ja" week-start="0" auto-apply
                minutes-grid-increment="1" enable-seconds seconds-grid-increment="1" :teleport="true"
                :format="formatDatetime" :clearable="false"
                :state="(isShowPeriodLargeErrorMessage ? false : null)"></vue-date-picker>
            </v-col>

            <v-col cols="12" class="mt-2" v-if="isShowPeriodLargeErrorMessage">
              <v-alert variant="tonal" type="error" v-model="isShowPeriodLargeErrorMessage" closable>
                出力対象期間は最長で1ヶ月間までです
              </v-alert>
            </v-col>

            <v-col cols="12" class="mt-2" v-if="isShowRequestCompleteMessage">
              <v-alert variant="tonal" type="success" v-model="isShowRequestCompleteMessage" closable>
                CSVファイルの作成をリクエストしました
              </v-alert>
            </v-col>
          </v-row>
        </v-form>

      </v-card-text>
      <v-card-actions class="py-7 pe-7 d-flex justify-end">
        <v-btn variant="flat" color="primary"
          :loading="isPostLoading" v-on:click="postCSVRequest">
          作成リクエスト
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import axios from 'axios';

import config from '@/config.js';

export default {
  name: 'CSVDownloadView',
  components: {
    VueDatePicker
  },

  setup() {

    const CSVRequests = ref([]);

    const isGetListLoading = ref(true);

    const form = ref();

    const modules = ref([
      { title: '未選択', value: '' },
      { title: 'すべて', value: '#' }
    ]);

    const moduleID = ref('');

    const now = new Date();

    const beginDatetime = ref(new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0
    ));

    const endDatetime = ref(new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    ));

    const isShowPeriodLargeErrorMessage = ref(false);
    const isShowRequestCompleteMessage = ref(false);

    const isPostLoading = ref(false);


    const formatDatetime = (date) => {

      const year = date.getFullYear().toString();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');

      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');

      return year + '-' + month + '-' + day + ' ' + hours + ':' + minutes + ':' + seconds;

    };


    let intervalID;

    onMounted(() => {

      fetchCSVRequestList();

      intervalID = setInterval(fetchCSVRequestList, 30000);


      fetchModuleList();

    });

    onBeforeUnmount(() => {

      clearInterval(intervalID);

    });

    const fetchModuleList = () => {

      axios.get(
        config.API_URL +
        `/users/me`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
      ).then((response) => {

        response.data.browsableModules.forEach((moduleID) => {
          modules.value.push({
            title: moduleID,
            value: moduleID
          });
        });

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };


    const fetchCSVRequestList = () => {

      isShowRequestCompleteMessage.value = false;

      isGetListLoading.value = true;

      axios.get(
        config.API_URL +
        `/users/me/csv-requests`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
      ).then((response) => {

        const now = new Date();

        response.data.CSVRequests.forEach((request) => {
          if (request.status === '作成中') {

            const requestTime = Date.parse(request.requestDatetime.replace(' ', 'T'));

            // リクエストから規定時間以内に作成完了していない場合は失敗したと見なす
            const threshold = (request.moduleID === '#') ? 600000 : 300000; // 10分 or 5分
            if ((now.getTime() - requestTime) > threshold) {
              request.status = '作成失敗';
            }

          }
        });

        CSVRequests.value = response.data.CSVRequests;

        isGetListLoading.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    }


    const postCSVRequest = async () => {

      isShowPeriodLargeErrorMessage.value = false;
      isShowRequestCompleteMessage.value = false;

      const { valid } = await form.value.validate();
      if (!valid) {
        return;
      }

      // 最長期間は31日間
      if ((endDatetime.value.getTime() - beginDatetime.value.getTime()) > 2678400000) {
        isShowPeriodLargeErrorMessage.value = true;
        return;
      }

      isPostLoading.value = true;

      const reqBody = {
        moduleID: moduleID.value,
        targetDatetime: {
          start: formatDatetime(beginDatetime.value),
          end: formatDatetime(endDatetime.value)
        }
      };

      axios.post(
        config.API_URL +
        `/users/me/csv-requests`,
        reqBody,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((response) => {

        moduleID.value = '';

        nextTick(() => {
          form.value.resetValidation();
        });

        isShowRequestCompleteMessage.value = true;

        CSVRequests.value.unshift(response.data);

        isPostLoading.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    }


    const download = (request) => {

      request.isDownloadStartLoading = true;

      const link = document.createElement('a');
      link.href = request.downloadURL;

      document.body.appendChild(link);

      link.click();

      link.parentNode.removeChild(link);

      setTimeout(() => {
        request.isDownloadStartLoading = false;
      }, 500);

    };


    return {
      CSVRequests,

      isGetListLoading,

      form,

      modules,
      moduleID,

      beginDatetime,
      endDatetime,

      isShowPeriodLargeErrorMessage,
      isShowRequestCompleteMessage,

      isPostLoading,


      formatDatetime,

      fetchCSVRequestList,

      postCSVRequest,

      download
    }

  }
}
</script>
